import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsBoolean,
  IsIn,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
} from 'class-validator';
import type { AppointmentAuthority } from '@munifocus/access';
import type { BylawCategory } from './files.dto';
import type { VisitStatus } from './visits.dto';

/**
 * Contractor operations — dispatch, My day, time tracking and the contractor's
 * monthly invoice to a municipality.
 *
 * Every route is gated twice: on the permission at the municipality, and on
 * the municipality having the module switched on (`contractorOpsEnabled`, off
 * by default). Officer-only, like the visits and tracking shapes: nothing here
 * is served by the public API.
 */

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The calendar day an instant falls on in New Brunswick, as `YYYY-MM-DD`. */
export function nbDay(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Moncton',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** The refusal code every operations route answers with where the module is off. */
export const OPERATIONS_DISABLED = 'operations_disabled' as const;

/** `GET /operations/status` — where the caller can use the module. */
export interface OperationsStatus {
  municipalities: Array<{
    localGovernmentId: string;
    name: string;
    /** Which of the operations permissions the caller holds there. */
    canDispatch: boolean;
    canReportTime: boolean;
    canApproveTime: boolean;
    canReadInvoices: boolean;
  }>;
  /** Companies whose invoices and rates the caller manages. */
  companies: Array<{ companyId: string; name: string }>;
}

// ── Dispatch ────────────────────────────────────────────────────────────────

/** One officer the dispatcher could give a file to. */
export interface DispatchOfficer {
  userId: string;
  displayName: string;
  badgeNumber: string;
  authorities: AppointmentAuthority[];
  /** The contractor the appointment is under; null for the town's own officer. */
  companyId: string | null;
  companyName: string | null;
  onDuty: boolean;
  /**
   * Straight-line metres from the officer's latest on-duty position to the
   * file, or null — not on duty, no position yet, or the caller may not see
   * positions (`tracking:view`).
   */
  distanceMeters: number | null;
  /** Open files the officer leads at this municipality. */
  openFiles: number;
}

export interface DispatchFile {
  fileId: string;
  reference: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  priority: 'routine' | 'elevated' | 'urgent';
  emergency: boolean;
  bylawCategory: BylawCategory;
  stage: string;
  assignedOfficerId: string | null;
  assignedOfficerName: string | null;
  createdAt: string;
}

/** `GET /operations/local-governments/:id/dispatch`. */
export interface DispatchBoard {
  localGovernmentId: string;
  /** Whether distances from live positions are shown (the caller holds `tracking:view`). */
  positionsVisible: boolean;
  /**
   * Set when the caller dispatches as a contractor rather than for the town:
   * only that company's officers, and only files unassigned or already theirs.
   */
  restrictedToCompanyIds: string[] | null;
  officers: DispatchOfficer[];
  files: DispatchFile[];
}

export type DispatchReason =
  'authority' | 'on_duty' | 'nearest' | 'light_workload' | 'current_assignee';

export interface DispatchSuggestion {
  officer: DispatchOfficer;
  /** Higher is better. Relative within one answer only. */
  score: number;
  /** What earned the score, strongest first. */
  reasons: DispatchReason[];
  /** False when the officer holds no authority the file's by-law calls for. */
  hasAuthority: boolean;
}

/** `POST /operations/local-governments/:id/dispatch/files/:fileId/assign`. */
export class AssignFileDto {
  @IsUUID()
  officerId!: string;
}

/**
 * The statute an officer should hold to work a file of this kind. A ranking
 * signal for the dispatcher — the hard gate on assignment stays the live
 * appointment (`canBeAssigned`). Dangerous and unsightly premises are a Local
 * Governance Act power; parking and the rest name none.
 */
export function requiredAuthorityFor(category: BylawCategory): AppointmentAuthority | null {
  return category === 'dangerous_unsightly' ? 'LGA' : null;
}

/**
 * Rank the officers who could take a file.
 *
 * Authority counts most (a file their appointment does not cover is the
 * wrong officer however close they are), then being on duty, then distance
 * among those with a position, then the lightest caseload. The current
 * assignee is marked so "reassign" reads as a choice.
 */
export function rankDispatchCandidates(
  officers: readonly DispatchOfficer[],
  file: Pick<DispatchFile, 'bylawCategory' | 'assignedOfficerId'>,
): DispatchSuggestion[] {
  const required = requiredAuthorityFor(file.bylawCategory);
  const distances = officers.map((o) => o.distanceMeters).filter((d): d is number => d !== null);
  const maxDistance = distances.length ? Math.max(...distances, 1) : 1;
  const maxLoad = Math.max(1, ...officers.map((o) => o.openFiles));

  return officers
    .map((officer) => {
      const hasAuthority = required === null || officer.authorities.includes(required);
      const parts: Array<[DispatchReason, number]> = [];
      if (required !== null && hasAuthority) parts.push(['authority', 4]);
      if (officer.onDuty) parts.push(['on_duty', 2]);
      if (officer.distanceMeters !== null) {
        parts.push(['nearest', 3 * (1 - officer.distanceMeters / maxDistance)]);
      }
      parts.push(['light_workload', 2 * (1 - officer.openFiles / maxLoad)]);
      const score = parts.reduce((sum, [, v]) => sum + v, 0) - (hasAuthority ? 0 : 10);
      const reasons = parts
        .filter(([, v]) => v > 0.25)
        .sort((a, b) => b[1] - a[1])
        .map(([r]) => r);
      if (file.assignedOfficerId === officer.userId) reasons.unshift('current_assignee');
      return { officer, score: Math.round(score * 100) / 100, reasons, hasAuthority };
    })
    .sort(
      (a, b) => b.score - a.score || a.officer.displayName.localeCompare(b.officer.displayName),
    );
}

// ── My day ──────────────────────────────────────────────────────────────────

export interface DayStop {
  fileId: string;
  reference: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  priority: 'routine' | 'elevated' | 'urgent';
  emergency: boolean;
  localGovernmentId: string;
  localGovernmentName: string;
  /** The leg arriving at this stop, from the previous one (or the start). */
  legDistanceMeters: number | null;
  legDurationSeconds: number | null;
  /** Encoded polyline (precision 5) of the leg. */
  legPolyline: string | null;
  /** Arrival if the officer set off now and spent the dwell time at each stop. */
  eta: string | null;
  /** The officer's visit to it today, if one has started. */
  visitStatus: VisitStatus | null;
}

/** `GET /operations/me/day`. */
export interface DayPlan {
  date: string;
  /** Where the route starts: the phone's fix, else the latest on-duty position. */
  start: { lat: number; lng: number } | null;
  startSource: 'device' | 'duty' | 'none';
  /** In visiting order. */
  stops: DayStop[];
  /** On the caseload but with no coordinates, so not on the route. */
  unrouted: DayStop[];
  /** Set aside for today by the officer. */
  skipped: DayStop[];
  /** True once the officer has reordered by hand. */
  manual: boolean;
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  /** True when any leg is a straight-line estimate rather than a road route. */
  estimated: boolean;
  /** Minutes assumed at each stop when computing ETAs. */
  dwellMinutes: number;
  /** Re-inspection appointments booked with the officer that day, soonest first. */
  appointments?: DayAppointment[];
}

/** A booked re-inspection on the officer's day. */
export interface DayAppointment {
  bookingId: string;
  fileId: string;
  reference: string;
  address: string;
  startsAt: string;
  endsAt: string;
}

/** Time assumed on site at each stop for the ETAs. */
export const DAY_PLAN_DWELL_MINUTES = 20;
/** Most stops a day is routed over; the rest are listed unrouted. */
export const DAY_PLAN_MAX_STOPS = 25;

export class DayPlanQueryDto {
  @IsOptional()
  @Matches(DATE_PATTERN)
  date?: string;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-90)
  @Max(90)
  lat?: number;

  @IsOptional()
  @IsNumber({ allowNaN: false, allowInfinity: false })
  @Min(-180)
  @Max(180)
  lng?: number;
}

/** `PUT /operations/me/day` — the officer's own order and set-asides. */
export class UpdateDayPlanDto {
  @IsOptional()
  @Matches(DATE_PATTERN)
  date?: string;

  @IsArray()
  @ArrayMaxSize(200)
  @IsUUID('all', { each: true })
  order!: string[];

  @IsArray()
  @ArrayMaxSize(200)
  @IsUUID('all', { each: true })
  skipped!: string[];
}

// ── Time ────────────────────────────────────────────────────────────────────

export type TimeEntrySource = 'manual' | 'visit' | 'duty';
export const TIME_ENTRY_SOURCES: TimeEntrySource[] = ['manual', 'visit', 'duty'];

export type TimeEntryStatus = 'submitted' | 'approved' | 'rejected';
export const TIME_ENTRY_STATUSES: TimeEntryStatus[] = ['submitted', 'approved', 'rejected'];

export interface TimeEntryView {
  id: string;
  officerId: string;
  officerName: string;
  localGovernmentId: string;
  companyId: string | null;
  fileId: string | null;
  fileReference: string | null;
  workDate: string;
  minutes: number;
  source: TimeEntrySource;
  sourceId: string | null;
  description: string;
  billable: boolean;
  status: TimeEntryStatus;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  invoiceId: string | null;
  /** On an invoice that has not been voided: nothing about it may change. */
  locked: boolean;
  createdAt: string;
  updatedAt: string;
}

/** An entry the officer has not recorded yet, proposed from what they did. */
export interface TimeSuggestion {
  source: Exclude<TimeEntrySource, 'manual'>;
  sourceId: string;
  localGovernmentId: string;
  fileId: string | null;
  fileReference: string | null;
  workDate: string;
  minutes: number;
  startedAt: string;
  endedAt: string;
}

export const TIME_ENTRY_MAX_MINUTES = 1440;

export class CreateTimeEntryDto {
  @IsUUID()
  localGovernmentId!: string;

  @IsOptional()
  @IsUUID()
  fileId?: string | null;

  @Matches(DATE_PATTERN)
  workDate!: string;

  @IsInt()
  @Min(1)
  @Max(TIME_ENTRY_MAX_MINUTES)
  minutes!: number;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  description?: string;

  @IsOptional()
  @IsBoolean()
  billable?: boolean;

  /** Set when the officer accepted a suggestion. */
  @IsOptional()
  @IsIn(['visit', 'duty'])
  source?: 'visit' | 'duty';

  @IsOptional()
  @IsUUID()
  sourceId?: string;
}

export class UpdateTimeEntryDto {
  @IsOptional()
  @IsUUID()
  fileId?: string | null;

  @IsOptional()
  @Matches(DATE_PATTERN)
  workDate?: string;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(TIME_ENTRY_MAX_MINUTES)
  minutes?: number;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  description?: string;

  @IsOptional()
  @IsBoolean()
  billable?: boolean;
}

export class ListTimeEntriesQueryDto {
  @IsOptional()
  @Matches(DATE_PATTERN)
  from?: string;

  @IsOptional()
  @Matches(DATE_PATTERN)
  to?: string;

  @IsOptional()
  @IsIn(TIME_ENTRY_STATUSES)
  status?: TimeEntryStatus;

  @IsOptional()
  @IsUUID()
  officerId?: string;
}

export class TimeSuggestionsQueryDto {
  @IsOptional()
  @Matches(DATE_PATTERN)
  from?: string;

  @IsOptional()
  @Matches(DATE_PATTERN)
  to?: string;
}

/** `POST /operations/local-governments/:id/time/review`. */
export class ReviewTimeEntriesDto {
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(200)
  @IsUUID('all', { each: true })
  ids!: string[];

  @IsIn(['approve', 'reject', 'reopen'])
  decision!: 'approve' | 'reject' | 'reopen';

  /** Required to reject: the officer is told why. */
  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}

/**
 * Whether an entry may still be edited, and by whom.
 *
 * An officer edits their own entry while it is waiting or after it was
 * rejected — editing a rejected entry sends it back for review. Approved time
 * is the supervisor's word and is reopened only by a supervisor; invoiced time
 * is frozen until its invoice is voided.
 */
export function canEditTimeEntry(
  entry: Pick<TimeEntryView, 'status' | 'locked'>,
  as: 'officer' | 'supervisor',
): boolean {
  if (entry.locked) return false;
  if (as === 'supervisor') return true;
  return entry.status !== 'approved';
}

// ── Invoices and rates ─────────────────────────────────────────────────────

export type ContractorInvoiceStatus = 'draft' | 'sent' | 'paid' | 'void';
export const CONTRACTOR_INVOICE_STATUSES: ContractorInvoiceStatus[] = [
  'draft',
  'sent',
  'paid',
  'void',
];
/** What a person is shown: `overdue` is derived, never stored. */
export type ContractorInvoiceDisplayStatus = ContractorInvoiceStatus | 'overdue';

export type ContractorInvoiceLineKind = 'time' | 'file_fee' | 'file_unlock';

export interface ContractorInvoiceLineView {
  id: string;
  kind: ContractorInvoiceLineKind;
  description: string;
  /** Minutes for a time line; a count otherwise. */
  quantity: number;
  unitAmountCents: number;
  amountCents: number;
  fileId: string | null;
}

export interface ContractorInvoiceView {
  id: string;
  companyId: string;
  companyName: string;
  localGovernmentId: string;
  localGovernmentName: string;
  /** `YYYY-MM`. */
  month: string;
  number: string;
  status: ContractorInvoiceStatus;
  displayStatus: ContractorInvoiceDisplayStatus;
  language: 'en' | 'fr';
  currency: string;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  taxRatePercent: number;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  paymentReference: string | null;
  notes: string;
  /** Included on the single-invoice read. */
  lines?: ContractorInvoiceLineView[];
  createdAt: string;
  updatedAt: string;
}

/** `POST /operations/companies/:companyId/invoices` — draft, or rebuild the draft. */
export class DraftInvoiceDto {
  @IsUUID()
  localGovernmentId!: string;

  @Matches(MONTH_PATTERN)
  month!: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  language?: 'en' | 'fr';

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  notes?: string;
}

export class MarkInvoicePaidDto {
  @IsOptional()
  @Matches(DATE_PATTERN)
  paidOn?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  paymentReference?: string;
}

export class ListInvoicesQueryDto {
  @IsOptional()
  @IsUUID()
  localGovernmentId?: string;

  @IsOptional()
  @IsIn(CONTRACTOR_INVOICE_STATUSES)
  status?: ContractorInvoiceStatus;
}

/** Which way an invoice may move. Draft is rebuilt in place, not "moved". */
export const CONTRACTOR_INVOICE_NEXT: Record<
  ContractorInvoiceStatus,
  readonly ContractorInvoiceStatus[]
> = {
  draft: ['sent', 'void'],
  sent: ['paid', 'void'],
  paid: [],
  void: [],
};

export function canMoveInvoice(
  from: ContractorInvoiceStatus,
  to: ContractorInvoiceStatus,
): boolean {
  return CONTRACTOR_INVOICE_NEXT[from].includes(to);
}

export interface RateCardView {
  companyId: string;
  companyName: string;
  localGovernmentId: string;
  localGovernmentName: string;
  /** False when no rates have been saved yet; the amounts are then the defaults. */
  configured: boolean;
  hourlyRateCents: number;
  fileFeeCents: number;
  unlockFeeCents: number | null;
  taxRatePercent: number;
  paymentTermsDays: number;
  currency: string;
  notes: string;
  updatedAt: string | null;
}

export const DEFAULT_TAX_RATE_PERCENT = 15;
export const DEFAULT_PAYMENT_TERMS_DAYS = 30;
/** $10,000.00 an hour: anything above is a typo. */
export const MAX_RATE_CENTS = 1_000_000;

export class UpsertRateCardDto {
  @IsInt()
  @Min(0)
  @Max(MAX_RATE_CENTS)
  hourlyRateCents!: number;

  @IsInt()
  @Min(0)
  @Max(MAX_RATE_CENTS)
  fileFeeCents!: number;

  /** Null to stop passing unlocks on. */
  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(MAX_RATE_CENTS)
  unlockFeeCents?: number | null;

  @IsNumber({ maxDecimalPlaces: 2, allowNaN: false, allowInfinity: false })
  @Min(0)
  @Max(100)
  taxRatePercent!: number;

  @IsInt()
  @Min(0)
  @Max(365)
  paymentTermsDays!: number;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  notes?: string;
}
